import React, { useContext, useEffect, useRef, useState } from "react";
import { FaCreditCard, FaLock, FaQrcode } from "react-icons/fa";
import { CartContext } from "../context/Cart";
import { useNavigate } from "react-router-dom";

const cardFields = [
  ["CARD_NUMBER", "card-number", "Card number"],
  ["CARD_DATE", "card-date", "Expiration date"],
  ["CARD_CVV", "card-cvv", "Security code"],
  ["CARD_POSTAL_CODE", "card-postal-code", "ZIP code"],
];

function loadCloverSdk(url) {
  if (window.Clover) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Clover card fields could not be loaded."));
    document.head.appendChild(script);
  });
}

const PaymentForm = ({ setActiveStep, setCompletedSteps, checkedValue, setCheckedValue, orderData, sweetAlert, setOrderData }) => {
  const { emptyCartItem, cartProducts } = useContext(CartContext);
  const navigate = useNavigate();
  const cloverRef = useRef(null);
  const paymentKeyRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (checkedValue !== "Credit Card") return undefined;
    let active = true;
    let fields = [];

    async function initialize() {
      setReady(false);
      setError("");
      try {
        const response = await fetch("/api/order/payment-config");
        if (!response.ok) throw new Error("Card payments are temporarily unavailable.");
        const config = await response.json();
        await loadCloverSdk(config.sdkUrl);
        if (!active) return;
        const clover = new window.Clover(config.publicToken, { merchantId: config.merchantId });
        const elements = clover.elements();
        fields = cardFields.map(([type, id]) => {
          const field = elements.create(type);
          field.mount(`#${id}`);
          return field;
        });
        cloverRef.current = clover;
        setReady(true);
      } catch (err) {
        if (active) setError(err.message);
      }
    }

    initialize();
    return () => {
      active = false;
      cloverRef.current = null;
      fields.forEach((field) => field.unmount?.());
    };
  }, [checkedValue]);

  async function submitOrder(token) {
    if (!paymentKeyRef.current) paymentKeyRef.current = window.crypto.randomUUID();
    const response = await fetch("/api/order/new", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...orderData, products: cartProducts, paymentType: checkedValue, token, idempotencyKey: paymentKeyRef.current }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.message || "Payment could not be completed.");
    paymentKeyRef.current = null;
    emptyCartItem();
    setActiveStep(0);
    setCompletedSteps([]);
    setOrderData({ ...orderData, paymentType: "" });
    sweetAlert("success", checkedValue === "Credit Card" ? "Payment successful!" : "Order placed. Payment is pending verification.");
    navigate("/thankyou");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      let token;
      if (checkedValue === "Credit Card") {
        if (!cloverRef.current) throw new Error("Card form is still loading.");
        const result = await cloverRef.current.createToken();
        if (result.errors) {
          const message = Object.values(result.errors).find(Boolean);
          throw new Error(typeof message === "string" ? message : "Please check your card details.");
        }
        token = result.token;
        if (!token) throw new Error("Card details could not be verified.");
      }
      await submitOrder(token);
    } catch (err) {
      setError(err.message || "Payment could not be completed.");
      sweetAlert("error", err.message || "Payment could not be completed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="step-content payment-step">
      <div className="payment-intro">
        <span className="payment-eyebrow">Final step</span>
        <h4>Choose your payment method</h4>
        <p className="info-form">Select how you would like to pay for your order.</p>
      </div>
      <ul className="payment-methods">
        <li className={`payment-method ${checkedValue === "Credit Card" ? "is-selected" : ""}`}>
          <div className="radio-wrapper">
            <input type="radio" name="payment-type" id="card-payment" checked={checkedValue === "Credit Card"} onChange={() => { setError(""); setCheckedValue("Credit Card"); }} />
            <label htmlFor="card-payment"><span className="payment-method-icon"><FaCreditCard aria-hidden="true" /></span><span className="payment-method-copy"><strong>Credit or debit card</strong><small>Pay securely with Clover</small></span><span className="payment-radio-mark" aria-hidden="true" /></label>
          </div>
          {checkedValue === "Credit Card" && (
            <form className="content payment-content" onSubmit={handleSubmit}>
              <div className="payment-content-heading"><span>Card details</span><span className="payment-secure-note"><FaLock aria-hidden="true" /> Secure checkout</span></div>
              <div className="clover-fields">
                {cardFields.map(([, id, label]) => (
                  <div key={id} className={`clover-field clover-field-${id}`}>
                    <span className="clover-label" id={`${id}-label`}>{label}</span>
                    <div id={id} className="clover-input" role="group" aria-labelledby={`${id}-label`} />
                  </div>
                ))}
              </div>
              {!ready && !error && <p className="payment-status" role="status">Loading secure card fields…</p>}
              {error && <p className="payment-error" role="alert">{error}</p>}
              <button type="submit" className="submit-btn payment-submit" disabled={!ready || busy}>{busy ? "Processing payment…" : "Pay securely"}</button>
              <p className="payment-reassurance"><FaLock aria-hidden="true" /> Your card details are encrypted and handled by Clover.</p>
            </form>
          )}
        </li>
        <li className={`payment-method ${checkedValue === "QR Code" ? "is-selected" : ""}`}>
          <div className="radio-wrapper">
            <input type="radio" name="payment-type" id="qr-payment" checked={checkedValue === "QR Code"} onChange={() => { setError(""); setCheckedValue("QR Code"); }} />
            <label htmlFor="qr-payment"><span className="payment-method-icon"><FaQrcode aria-hidden="true" /></span><span className="payment-method-copy"><strong>QR code</strong><small>Pay by scanning the code</small></span><span className="payment-radio-mark" aria-hidden="true" /></label>
          </div>
          {checkedValue === "QR Code" && (
            <div className="content payment-content payment-qr-content">
              <p>Scan the QR code to complete payment. Your order will be confirmed after payment is verified.</p>
              <img src="/admin/assets/images/qr-code.png" alt="Payment QR code" className="payment-qr-image" />
              {error && <p className="payment-error" role="alert">{error}</p>}
              <button type="button" onClick={handleSubmit} className="submit-btn payment-submit" disabled={busy}>{busy ? "Processing…" : "I have completed payment"}</button>
            </div>
          )}
        </li>
      </ul>
    </div>
  );
};

export default PaymentForm;
